import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import { 
    FileText, 
    Printer, 
    TrendingUp, 
    TrendingDown, 
    DollarSign, 
    PieChart,
    BarChart3,
    Activity,
    Bus,
    Edit2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Input } from '../../components/ui/index';
import { Modal } from '../../components/ui/Modal';
import { 
    PieChart as RechartsPieChart, 
    Pie, 
    Cell, 
    Tooltip, 
    Legend, 
    ResponsiveContainer 
} from 'recharts';

const COLORS = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#64748b'];

const FinancialReports = () => {
    const { selectedYear } = useAuth();
    const { showToast } = useToast();
    const [activeTab, setActiveTab] = useState('summary');
    const [loading, setLoading] = useState(true);
    const [reportData, setReportData] = useState(null);

    // Print Modal States
    const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
    const [printTab, setPrintTab] = useState(null);
    const [printDates, setPrintDates] = useState({ startDate: '', endDate: '' });
    const [isPrinting, setIsPrinting] = useState(false);

    // Update Balance Modal States
    const [isBalanceModalOpen, setIsBalanceModalOpen] = useState(false);
    const [newPreviousBalance, setNewPreviousBalance] = useState('');
    const [isUpdatingBalance, setIsUpdatingBalance] = useState(false);

    useEffect(() => {
        fetchReportData();
    }, [selectedYear]);

    const fetchReportData = async () => {
        if (!selectedYear) return;
        setLoading(true);
        try {
            const response = await api.get(`/reports/financial?academic_year_id=${selectedYear.id}`);
            setReportData(response.data);
        } catch (error) {
            console.error('Error fetching report:', error);
            showToast('حدث خطأ أثناء تحميل البيانات المالية', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenBalanceModal = (currentBalance) => {
        setNewPreviousBalance(currentBalance || '');
        setIsBalanceModalOpen(true);
    };

    const submitUpdateBalance = async () => {
        if (!selectedYear) return;
        setIsUpdatingBalance(true);
        try {
            await api.put(`/academic-years/${selectedYear.id}`, {
                previous_balance: newPreviousBalance
            });
            showToast('تم تحديث الرصيد السابق بنجاح', 'success');
            setIsBalanceModalOpen(false);
            fetchReportData();
        } catch (error) {
            console.error('Error updating previous balance:', error);
            showToast('حدث خطأ أثناء تحديث الرصيد', 'error');
        } finally {
            setIsUpdatingBalance(false);
        }
    };

    const handlePrint = (type) => {
        if (!reportData) return;
        setPrintTab(type);
        setPrintDates({
            startDate: reportData.academic_year.start_date || '',
            endDate: reportData.academic_year.end_date || ''
        });
        setIsPrintModalOpen(true);
    };

    const confirmPrint = async () => {
        if (!selectedYear) return;
        setIsPrinting(true);
        try {
            const response = await api.get('/reports/financial', {
                params: {
                    academic_year_id: selectedYear.id,
                    start_date: printDates.startDate,
                    end_date: printDates.endDate
                }
            });
            let path = '';
            if (printTab === 'summary') path = '/print/financial-report';
            if (printTab === 'expenses') path = '/print/expenses-detail';
            if (printTab === 'revenues') path = '/print/revenues-detail';
            if (printTab === 'bus_expenses') {
                const busResponse = await api.get('/reports/bus-expenses', {
                    params: {
                        start_date: printDates.startDate,
                        end_date: printDates.endDate
                    }
                });
                localStorage.setItem('print_bus_expenses_report', JSON.stringify({
                    ...busResponse.data,
                    print_start_date: printDates.startDate,
                    print_end_date: printDates.endDate
                }));
                path = '/print/bus-expenses';
            } else {
                localStorage.setItem('print_financial_report', JSON.stringify({
                    ...response.data,
                    print_start_date: printDates.startDate,
                    print_end_date: printDates.endDate
                }));
            }

            window.open(path, '_blank');
            setIsPrintModalOpen(false);
        } catch (error) {
            console.error('Error fetching print data:', error);
            showToast('حدث خطأ أثناء تحضير بيانات الطباعة', 'error');
        } finally {
            setIsPrinting(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', { style: 'currency', currency: 'MAD' }).format(amount || 0);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                <p className="text-slate-500 font-medium">جاري تحميل البيانات المالية...</p>
            </div>
        );
    }

    if (!reportData) {
        return (
            <div className="text-center py-12 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-20" />
                <p>لا توجد بيانات متاحة للسنة المحددة.</p>
            </div>
        );
    }

    const tabs = [
        { id: 'summary', label: 'التقرير المالي', icon: Activity },
        { id: 'expenses', label: 'تفاصيل المصاريف', icon: TrendingDown },
        { id: 'revenues', label: 'تفاصيل المداخيل', icon: TrendingUp },
        { id: 'bus_expenses', label: 'مصاريف الحافلات', icon: Bus },
    ];

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl  border border-slate-100">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center">
                        <PieChart className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-base font-medium text-slate-800">إحصائيات والتقارير المالية</h1>
                        <p className="text-slate-500 text-sm mt-1">
                            السنة الدراسية: {reportData.academic_year.name}
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs & Content */}
            <div className="bg-white rounded-2xl  border border-slate-100 overflow-hidden">
                <div className="flex flex-col sm:flex-row items-center justify-between border-b border-slate-100 px-4">
                    <div className="flex items-center overflow-x-auto w-full sm:w-auto no-scrollbar">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap
                                        ${isActive 
                                            ? 'border-primary text-primary' 
                                            : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                                        }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </div>
                    {/* Print Button (Far Left) */}
                    <div className="py-2 sm:py-0 w-full sm:w-auto flex justify-end">
                        <Button 
                            onClick={() => handlePrint(activeTab)}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white  font-medium text-sm"
                        >
                            <Printer className="w-4 h-4 ml-2" />
                            استخراج ملف PDF
                        </Button>
                    </div>
                </div>

                <div className="p-6">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={activeTab}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                        >
                            {activeTab === 'summary' && <SummaryTab data={reportData} formatCurrency={formatCurrency} onOpenBalanceModal={handleOpenBalanceModal} />}
                            {activeTab === 'expenses' && <ExpensesTab data={reportData} formatCurrency={formatCurrency} />}
                            {activeTab === 'revenues' && <RevenuesTab data={reportData} formatCurrency={formatCurrency} />}
                            {activeTab === 'bus_expenses' && <BusExpensesTab formatCurrency={formatCurrency} />}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>

            {/* Print Modal */}
            <Modal 
                isOpen={isPrintModalOpen} 
                onClose={() => setIsPrintModalOpen(false)}
                title="تحديد فترة التقرير للطباعة"
                size="md"
            >
                <div className="space-y-4">
                    <p className="text-sm text-slate-500 mb-4">المرجو تحديد الفترة الزمنية التي تريد طباعة تقريرها.</p>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            label="من تاريخ"
                            type="date"
                            value={printDates.startDate}
                            onChange={(e) => setPrintDates({ ...printDates, startDate: e.target.value })}
                        />
                        <Input
                            label="إلى تاريخ"
                            type="date"
                            value={printDates.endDate}
                            onChange={(e) => setPrintDates({ ...printDates, endDate: e.target.value })}
                        />
                    </div>

                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                        <Button 
                            variant="secondary" 
                            onClick={() => setIsPrintModalOpen(false)}
                            disabled={isPrinting}
                        >
                            إلغاء
                        </Button>
                        <Button 
                            onClick={confirmPrint}
                            isLoading={isPrinting}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                            تأكيد واستخراج
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Update Balance Modal */}
            <Modal 
                isOpen={isBalanceModalOpen} 
                onClose={() => setIsBalanceModalOpen(false)}
                title="تعديل الرصيد السابق (مرحل)"
                size="md"
            >
                <div className="space-y-4">
                    <Input
                        label="المبلغ"
                        type="number"
                        min="0"
                        step="0.01"
                        value={newPreviousBalance}
                        onChange={(e) => setNewPreviousBalance(e.target.value)}
                    />
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                        <Button 
                            variant="secondary" 
                            onClick={() => setIsBalanceModalOpen(false)}
                            disabled={isUpdatingBalance}
                        >
                            إلغاء
                        </Button>
                        <Button 
                            onClick={submitUpdateBalance}
                            isLoading={isUpdatingBalance}
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        >
                            حفظ الرصيد
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

// --- Sub-components for Tabs ---

const SummaryTab = ({ data, formatCurrency, onOpenBalanceModal }) => {
    const { summary, expenses, revenues } = data;

    const chartData = [
        { name: 'المداخيل (بدون الرصيد السابق)', value: revenues.total },
        { name: 'المصاريف', value: expenses.total }
    ];

    return (
        <div className="space-y-8">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                            <DollarSign className="w-5 h-5" />
                        </div>
                        <h3 className="text-sm font-medium text-slate-600">الرصيد السابق</h3>
                    </div>
                    <p className="text-base font-medium text-slate-800">{formatCurrency(summary.previous_balance)}</p>
                </div>

                <div className="bg-green-50 p-5 rounded-2xl border border-green-100">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-green-200 text-green-700 flex items-center justify-center">
                            <TrendingUp className="w-5 h-5" />
                        </div>
                        <h3 className="text-sm font-medium text-green-800">مجموع المداخيل العامة</h3>
                    </div>
                    <p className="text-base font-medium text-green-700">{formatCurrency(summary.total_revenues_general)}</p>
                </div>

                <div className="bg-red-50 p-5 rounded-2xl border border-red-100">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-lg bg-red-200 text-red-700 flex items-center justify-center">
                            <TrendingDown className="w-5 h-5" />
                        </div>
                        <h3 className="text-sm font-medium text-red-800">مجموع المصاريف</h3>
                    </div>
                    <p className="text-base font-medium text-red-700">{formatCurrency(expenses.total)}</p>
                </div>

                <div className={`p-5 rounded-2xl border ${summary.net_result >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                    <div className="flex items-center gap-3 mb-2">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${summary.net_result >= 0 ? 'bg-emerald-200 text-emerald-700' : 'bg-rose-200 text-rose-700'}`}>
                            <Activity className="w-5 h-5" />
                        </div>
                        <h3 className={`text-sm font-medium ${summary.net_result >= 0 ? 'text-emerald-800' : 'text-rose-800'}`}>النتيجة الصافية</h3>
                    </div>
                    <p className={`text-base font-medium ${summary.net_result >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                        {formatCurrency(summary.net_result)}
                    </p>
                </div>
            </div>

            {/* Visuals */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                    <h3 className="text-base font-medium text-slate-800 mb-6 flex items-center gap-2">
                        <PieChart className="w-5 h-5 text-primary" />
                        نظرة عامة (مداخيل السنة / المصاريف)
                    </h3>
                    <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <RechartsPieChart>
                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={80}
                                    outerRadius={110}
                                    paddingAngle={5}
                                    dataKey="value"
                                >
                                    <Cell fill="#10b981" />
                                    <Cell fill="#ef4444" />
                                </Pie>
                                <Tooltip formatter={(value) => formatCurrency(value)} />
                                <Legend />
                            </RechartsPieChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                    <h3 className="text-base font-medium text-slate-800 mb-6">ملخص سريع</h3>
                    <div className="space-y-4">
                        <div className="flex justify-between items-center p-4 bg-white rounded-xl border border-slate-200 ">
                            <div className="flex items-center gap-2">
                                <span className="font-medium text-slate-600">الرصيد السابق (مرحل)</span>
                                <button onClick={() => onOpenBalanceModal(summary.previous_balance)} className="text-slate-400 hover:text-indigo-600 transition-colors" title="تعديل الرصيد">
                                    <Edit2 className="w-4 h-4" />
                                </button>
                            </div>
                            <span className="font-medium text-base">{formatCurrency(summary.previous_balance)}</span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-white rounded-xl border border-slate-200 ">
                            <span className="font-medium text-slate-600">مجموع مداخيل الفترة</span>
                            <span className="font-medium text-base text-green-600">+{formatCurrency(revenues.total)}</span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-white rounded-xl border border-slate-200 ">
                            <span className="font-medium text-slate-600">مجموع مصاريف الفترة</span>
                            <span className="font-medium text-base text-red-500">-{formatCurrency(expenses.total)}</span>
                        </div>
                        <div className="flex justify-between items-center p-4 bg-slate-800 text-white rounded-xl  mt-6">
                            <span className="font-medium">النتيجة الصافية</span>
                            <span className="font-medium text-base">{formatCurrency(summary.net_result)}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ExpensesTab = ({ data, formatCurrency }) => {
    const { expenses } = data;
    
    // Filter out zero amount
    const validExpenses = expenses.breakdown.filter(e => e.amount > 0);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
                <div className="overflow-x-auto rounded-xl border border-slate-200 ">
                    <table className="w-full text-right text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-medium">
                            <tr>
                                <th className="px-6 py-4">نوع المصاريف</th>
                                <th className="px-6 py-4">المبلغ</th>
                                <th className="px-6 py-4">النسبة %</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                            {validExpenses.length > 0 ? (
                                validExpenses.map((expense, idx) => (
                                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-3.5 font-semibold text-slate-700">{expense.type_name}</td>
                                        <td className="px-6 py-3.5 font-medium text-slate-900">{formatCurrency(expense.amount)}</td>
                                        <td className="px-6 py-3.5">
                                            <div className="flex items-center gap-2">
                                                <span className="text-slate-600 font-medium w-12">{expense.percentage}%</span>
                                                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
                                                    <div 
                                                        className="h-full bg-red-400 rounded-full" 
                                                        style={{ width: `${expense.percentage}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3" className="px-6 py-8 text-center text-slate-500">لا توجد مصاريف مسجلة في هذه الفترة</td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot className="bg-red-50/50 border-t border-red-100">
                            <tr>
                                <td className="px-6 py-4 font-medium text-red-800 text-base">مجموع المصاريف</td>
                                <td className="px-6 py-4 font-medium text-red-700 text-base" colSpan="2">{formatCurrency(expenses.total)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 h-[400px] flex flex-col">
                 <h3 className="font-medium text-slate-700 mb-4 text-center">توزيع المصاريف</h3>
                 <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                        <Pie
                            data={validExpenses}
                            cx="50%"
                            cy="45%"
                            outerRadius={95}
                            minAngle={5}
                            dataKey="amount"
                            nameKey="type_name"
                            labelLine={false}
                        >
                            {validExpenses.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value, name, item) => [formatCurrency(value) + ` (${item.payload.percentage || 0}%)`, name]} />
                        <Legend verticalAlign="bottom" height={36} formatter={(value) => <span className="text-xs font-medium text-slate-700">{value}</span>} />
                    </RechartsPieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

const RevenuesTab = ({ data, formatCurrency }) => {
    const { revenues } = data;
    
    const treasuryFeedItem = revenues.breakdown.find(r => r.source === 'تغذية الخزينة');
    const treasuryFeedAmount = treasuryFeedItem ? parseFloat(treasuryFeedItem.amount) || 0 : 0;
    
    const rawValidRevenues = revenues.breakdown.filter(r => parseFloat(r.amount) > 0 && r.source !== 'تغذية الخزينة');
    const adjustedTotalRevenues = rawValidRevenues.reduce((acc, curr) => acc + (parseFloat(curr.amount) || 0), 0);

    const validRevenues = rawValidRevenues.map(r => {
        const amt = parseFloat(r.amount) || 0;
        const pct = adjustedTotalRevenues > 0 ? ((amt / adjustedTotalRevenues) * 100).toFixed(2) : '0.00';
        return {
            ...r,
            amount: amt,
            percentage: pct
        };
    });

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
                <div className="overflow-x-auto rounded-xl border border-slate-200 ">
                    <table className="w-full text-right text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-medium">
                            <tr>
                                <th className="px-6 py-4">نوع المداخيل</th>
                                <th className="px-6 py-4">المبلغ</th>
                                <th className="px-6 py-4">النسبة %</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                            {validRevenues.length > 0 ? (
                                validRevenues.map((revenue, idx) => (
                                    <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-3.5 font-semibold text-slate-700">{revenue.source}</td>
                                        <td className="px-6 py-3.5 font-medium text-slate-900">{formatCurrency(revenue.amount)}</td>
                                        <td className="px-6 py-3.5">
                                            <div className="flex items-center gap-2">
                                                <span className="text-slate-600 font-medium w-12">{revenue.percentage}%</span>
                                                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
                                                    <div 
                                                        className="h-full bg-emerald-400 rounded-full" 
                                                        style={{ width: `${Math.min(parseFloat(revenue.percentage), 100)}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="3" className="px-6 py-8 text-center text-slate-500">لا توجد مداخيل مسجلة في هذه الفترة</td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot className="bg-emerald-50/50 border-t border-emerald-100">
                            <tr>
                                <td className="px-6 py-4 font-medium text-emerald-800 text-base">مجموع المداخيل</td>
                                <td className="px-6 py-4 font-medium text-emerald-700 text-base" colSpan="2">{formatCurrency(adjustedTotalRevenues)}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
            <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100 h-[400px] flex flex-col">
                 <h3 className="font-medium text-slate-700 mb-4 text-center">توزيع المداخيل</h3>
                 <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                        <Pie
                            data={validRevenues}
                            cx="50%"
                            cy="45%"
                            outerRadius={95}
                            minAngle={5}
                            dataKey="amount"
                            nameKey="source"
                            labelLine={false}
                        >
                            {validRevenues.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip formatter={(value, name, item) => [formatCurrency(value) + ` (${item.payload.percentage}%)`, name]} />
                        <Legend verticalAlign="bottom" height={36} formatter={(value) => <span className="text-xs font-medium text-slate-700">{value}</span>} />
                    </RechartsPieChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

const BusExpensesTab = ({ formatCurrency }) => {
    const { selectedYear } = useAuth();
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        busId: ''
    });
    const [buses, setBuses] = useState([]);

    useEffect(() => {
        fetchBuses();
    }, []);

    useEffect(() => {
        fetchBusExpenses();
    }, [filters, selectedYear]);

    const fetchBuses = async () => {
        try {
            const response = await api.get('/buses');
            setBuses(response.data.data || response.data || []);
        } catch (error) {
            console.error('Error fetching buses:', error);
        }
    };

    const fetchBusExpenses = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.startDate) params.start_date = filters.startDate;
            if (filters.endDate) params.end_date = filters.endDate;
            if (filters.busId) params.bus_id = filters.busId;
            
            const response = await api.get('/reports/bus-expenses', { params });
            setData(response.data);
        } catch (error) {
            console.error('Error fetching bus expenses:', error);
            showToast('حدث خطأ أثناء تحميل مصاريف الحافلات', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Filters */}
            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                    <Input
                        label="من تاريخ"
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                    />
                </div>
                <div className="flex-1 min-w-[200px]">
                    <Input
                        label="إلى تاريخ"
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                    />
                </div>
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-slate-700 mb-1">الحافلة</label>
                    <select
                        className="w-full h-10 px-3 rounded-lg border border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
                        value={filters.busId}
                        onChange={(e) => setFilters({ ...filters, busId: e.target.value })}
                    >
                        <option value="">جميع الحافلات</option>
                        {buses.map(bus => (
                            <option key={bus.id} value={bus.id}>
                                {bus.matricule} {bus.code ? `(${bus.code})` : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : !data || data.buses_report.length === 0 ? (
                <div className="text-center py-12 text-slate-500">لا توجد مصاريف للحافلات في هذه الفترة</div>
            ) : (
                <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full text-right text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200 text-slate-600 font-medium">
                                    <tr>
                                        <th className="px-6 py-4">رقم الحافلة</th>
                                        <th className="px-6 py-4">نوع المصاريف</th>
                                        <th className="px-6 py-4">مبلغ المصاريف</th>
                                        <th className="px-6 py-4 text-center">توزيع المصاريف</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                    {data.buses_report.map((busReport, idx) => {
                                        const busChartData = busReport.expenses.map(e => ({ name: e.type_name, value: parseFloat(e.amount) }));
                                        return (
                                        <React.Fragment key={idx}>
                                            {busReport.expenses.map((exp, expIdx) => (
                                                <tr key={`${idx}-${expIdx}`} className="hover:bg-slate-50/50">
                                                    {expIdx === 0 && (
                                                        <td className="px-6 py-3.5 font-semibold text-slate-700" rowSpan={busReport.expenses.length} style={{ verticalAlign: 'middle' }}>
                                                            {busReport.bus_matricule}
                                                        </td>
                                                    )}
                                                    <td className="px-6 py-3.5 text-slate-700">
                                                        <div className="flex items-center gap-2">
                                                            <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: COLORS[expIdx % COLORS.length] }}></span>
                                                            {exp.type_name}
                                                        </div>
                                                    </td>
                                                    <td className="px-6 py-3.5 font-medium">{formatCurrency(exp.amount)}</td>
                                                    {expIdx === 0 && (
                                                        <td className="px-6 py-3.5" rowSpan={busReport.expenses.length}>
                                                            <div className="h-[120px] w-full min-w-[140px] flex justify-center">
                                                                <ResponsiveContainer width="100%" height="100%">
                                                                    <RechartsPieChart>
                                                                        <Pie
                                                                            data={busChartData}
                                                                            cx="50%"
                                                                            cy="50%"
                                                                            innerRadius={30}
                                                                            outerRadius={40}
                                                                            dataKey="value"
                                                                            nameKey="name"
                                                                            labelLine={false}
                                                                            label={({ cx, cy, midAngle, outerRadius, percent }) => {
                                                                                if (percent < 0.01) return null;
                                                                                const RADIAN = Math.PI / 180;
                                                                                const radius = outerRadius + 10;
                                                                                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                                                                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                                                                return (
                                                                                    <text x={x} y={y} fill="#475569" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={10} fontWeight="bold">
                                                                                        {`${(percent * 100).toFixed(0)}%`}
                                                                                    </text>
                                                                                );
                                                                            }}
                                                                        >
                                                                            {busChartData.map((entry, index) => (
                                                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                                            ))}
                                                                        </Pie>
                                                                        <Tooltip formatter={(value) => formatCurrency(value)} />
                                                                    </RechartsPieChart>
                                                                </ResponsiveContainer>
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                            <tr className="bg-yellow-50/50">
                                                <td className="px-6 py-3 font-semibold text-yellow-800" colSpan="2">
                                                    مجموع مصاريف {busReport.bus_matricule}
                                                </td>
                                                <td className="px-6 py-3 font-semibold text-yellow-800">{formatCurrency(busReport.total)}</td>
                                                <td className="px-6 py-3"></td>
                                            </tr>
                                        </React.Fragment>
                                        );
                                    })}
                                </tbody>
                                <tfoot className="bg-blue-50/50 border-t border-blue-100">
                                    <tr>
                                        <td className="px-6 py-4 font-bold text-blue-800 text-base" colSpan="2">مجموع مصاريف الحافلات</td>
                                        <td className="px-6 py-4 font-bold text-blue-800 text-base">{formatCurrency(data.grand_total)}</td>
                                        <td className="px-6 py-4"></td>
                                    </tr>
                                </tfoot>
                            </table>
                </div>
            )}
        </div>
    );
};

export default FinancialReports;
